<template>
  <section>
    <Topbar />
    <Header />
    <Sidebar />
    <main>
      <el-alert
        v-if="isDev"
        title="OpenFreight Development"
        type="warning"
        center
        show-icon
        :closable="false"
      />
      <el-alert
        v-if="isQA"
        title="OpenFreight QA"
        type="warning"
        center
        show-icon
        :closeable="false"
      />
      <RouterView
        :key="viewKey"
        class="p-3"
      />
      <p class="m-0 pb-3 pr-3 text-muted text-right font-weight-lighter">
        OpenFreight Build b92f08c<br>
        &copy; {{ (new Date()).getFullYear() }} Open360. All Rights Reserved
      </p>
    </main>
  </section>
</template>

<script>
import Session from '@/plugins/session'
import Topbar from '@/components/Navigation/Topbar'
import Header from '@/components/Navigation/Header'
import Sidebar from '@/components/Navigation/Sidebar'
import FreightIcon from '@/assets/images/freight.svg'
import CustomerServiceIcon from '@/assets/images/customer-service.svg'
import ConfigurationIcon from '@/assets/images/configuration.svg'
import SalesIcon from '@/assets/images/sales.svg'
import ReportsIcon from '@/assets/images/reports.svg'
import ManageIcon from '@/assets/images/manage.svg'
import BillingIcon from '@/assets/images/billing.svg'
import { READ } from '@/plugins/user'
import { Notification, Message } from 'element-ui'

export default {
  components: {
    Topbar,
    Header,
    Sidebar
  },
  data () {
    return {
      open360DialogVisible: false,
      isQA: process.env.VUE_APP_SENTRY_ENVIRONMENT === 'Staging',
      isDev: process.env.VUE_APP_SENTRY_ENVIRONMENT === 'Development',
      applications: [
        {
          hidden: this.$user.cannot(READ, 'menu_customerservice'),
          icon: CustomerServiceIcon,
          route: { name: 'customerservice' },
          name: 'Customer Service'
        },
        {
          hidden: this.$user.cannot(READ, 'menu_manage'),
          icon: ManageIcon,
          route: { name: 'manage' },
          name: 'Manage'
        },
        {
          hidden: this.$user.cannot(READ, 'menu_sales'),
          icon: SalesIcon,
          route: { name: 'sales' },
          name: 'Sales'
        },
        {
          hidden: this.$user.cannot(READ, 'menu_freight'),
          icon: FreightIcon,
          route: { name: 'freight' },
          name: 'Freight'
        },
        {
          hidden: this.$user.cannot(READ, 'menu_billing'),
          icon: BillingIcon,
          route: { name: 'billing' },
          name: 'Billing'
        },
        {
          hidden: this.$user.cannot(READ, 'menu_reports'),
          icon: ReportsIcon,
          route: { name: 'reports' },
          name: 'Reports'
        },
        {
          hidden: this.$user.cannot(READ, 'menu_configuration'),
          icon: ConfigurationIcon,
          route: { name: 'configuration' },
          name: 'Configuration'
        }
      ],
      viewKey: 1,
      routeHistory: [],
      routeFuture: [],
      isShortcutNavigation: false,
      originalMousetrapStopCallback: null
    }
  },
  watch: {
    $route (newRoute, existingRoute) {
      if (newRoute.name === existingRoute.name || !this.$_.isEqual(newRoute.params, existingRoute.params)) {
        this.viewKey += 1
      }

      if (this.isShortcutNavigation) {
        this.isShortcutNavigation = false
        return
      }

      const newPath = newRoute.fullPath
      const lastPath = this.routeHistory[this.routeHistory.length - 1]

      if (newPath && newPath !== lastPath) {
        this.routeHistory.push(newPath)
        this.routeFuture = []
      }
    }
  },
  mounted () {
    window.addEventListener('message', function (event) {
      if (event.origin !== window.location.origin) {
        return // Ignore messages from different origins
      }

      const payload = event.data || {}

      if (payload.type === 'alfredHotKeyTrigger') {
        window.dispatchEvent(new Event('alfred:open'))
        return
      }

      const { channel, data } = payload
      if (channel) {
        // eslint-disable-next-line
        Message({
          message: data.text ? data.text : '',
          type: data.type ? data.type : 'info',
          duration: data.duration ? data.duration : 15000,
          showClose: true,
          center: true
        })
      }
    })
    this.routeHistory = [this.$route.fullPath]
    this.bindKeys()
  },
  beforeDestroy () {
    this.unbindKeys()

    if (typeof this.originalMousetrapStopCallback === 'function') {
      this.$mousetrap.stopCallback = this.originalMousetrapStopCallback
      this.originalMousetrapStopCallback = null
    }
  },
  methods: {
    routerPush (route) {
      this.$router.push({
        name: route,
        params: null
      })
    },
    goBackInApp () {
      if (this.routeHistory.length > 1) {
        const currentPath = this.routeHistory.pop()
        if (currentPath) {
          this.routeFuture.push(currentPath)
        }

        const previousPath = this.routeHistory[this.routeHistory.length - 1]

        if (previousPath) {
          this.isShortcutNavigation = true
          this.$router.push(previousPath).catch(() => {})
          return
        }
      }

      this.routerPush(this.$store.getters['configuration/layout/central'] ? this.$store.getters['configuration/layout/central'] : 'customerservice')
    },
    goForwardInApp () {
      if (!this.routeFuture.length) {
        return
      }

      const nextPath = this.routeFuture.pop()

      if (!nextPath) {
        return
      }

      const currentPath = this.routeHistory[this.routeHistory.length - 1]

      if (nextPath !== currentPath) {
        this.routeHistory.push(nextPath)
      }

      this.isShortcutNavigation = true
      this.$router.push(nextPath).catch(() => {})
    },
    enableGlobalShortcuts () {
      if (typeof this.originalMousetrapStopCallback !== 'function') {
        this.originalMousetrapStopCallback = this.$mousetrap.stopCallback
      }

      const globalCombos = ['`', 'esc', 'g h', 'alt+left', 'alt+right']

      this.$mousetrap.stopCallback = (event, element, combo) => {
        if (combo && globalCombos.includes(combo)) {
          return false
        }

        return this.originalMousetrapStopCallback
          ? this.originalMousetrapStopCallback(event, element, combo)
          : false
      }
    },
    async bindKeys () {
      this.enableGlobalShortcuts()

      this.$mousetrap.bind('`', (event) => {
        event.preventDefault()
        document.getElementById('search').focus()
      })
      this.$mousetrap.bind('esc', (event) => {
        event.preventDefault()
        // eslint-disable-next-line
        Message.closeAll()
        // eslint-disable-next-line
        Notification.closeAll()
      })

      this.$mousetrap.bind('g h', (event) => {
        event.preventDefault()
        this.routerPush(this.$store.getters['configuration/layout/central'] ? this.$store.getters['configuration/layout/central'] : 'customerservice')
      })

      await Session.active
      // check preferences for keyboard shortcuts
      this.configuration = this.$_.cloneDeep(this.$store.getters.configuration)

      if (this.configuration.layout.keyboardShortcutsEnabled) {
        console.log('Binding keyboard shortcuts')
        // alt+ arrow to navigate router back and forward history
        this.$mousetrap.bind('alt+left', (event) => {
          event.preventDefault()
          this.goBackInApp()
        })
        this.$mousetrap.bind('alt+right', (event) => {
          event.preventDefault()
          this.goForwardInApp()
        })
      } else {
        console.log('Global keyboard shortcuts are disabled by user preference, not binding')
      }
    },
    unbindKeys () {
      this.$mousetrap.unbind('`')
      this.$mousetrap.unbind('esc')
      this.$mousetrap.unbind('g h')
      this.$mousetrap.unbind('alt+left')
      this.$mousetrap.unbind('alt+right')
    },
    selected (event) {
      console.log('selected', event.detail)
    },
    change (event) {
      console.log('change', event.detail)
    }
  },
  async beforeRouteEnter (to, from, next) {
    let redirect

    if (await Session.active) {
      // If the session is active, always refresh
      // the session on the first "load" of the
      // application.
      const refreshed = await Session.refresh()
      if (!refreshed) {
        await Session.terminate()
      }
    }
    if (await Session.inactive) {
      // If the session is inactive redirect the user
      // to the signin page.
      redirect = {
        name: 'authentication-signin',
        query: { redirect: to.fullPath },
        replace: true
      }
    } else if ((await Session.instance).team.open360_only || (await Session.instance).profile.firebase_id) {
      this.open360DialogVisible = true
      if ((await Session.instance).team.legacy.shipper) {
        redirect = {
          name: 'legacy',
          query: { tenancy: (await Session.instance).tenancy.id, token: await Session.token },
          replace: true
        }
      }
      // The session is active, check if the user is a
      // "Open360" user. If they are, redirect them to
      // // the Open360 application.
      // let url = ''
      // switch (process.env.VUE_APP_SENTRY_ENVIRONMENT) {
      //   case 'Development':
      //     url = 'https://staging.app.open360.com/' + ((await Session.instance).account ? (await Session.instance).account.slug : '') + '/login'
      //     break
      //   case 'Staging':
      //   case 'UAT':
      //   case 'QA':
      //     url = 'https://staging.app.open360.com/' + ((await Session.instance).account ? (await Session.instance).account.slug : '') + '/login'
      //     break
      //   case 'Production':
      //     url = 'https://app.open360.com/' + ((await Session.instance).account ? (await Session.instance).account.slug : '') + '/login'
      //     break
      // }

      // window.location.href = url
    } else if ((await Session.instance).team.legacy.shipper) {
      // The session is active, check if the user is a legacy
      // "shipper" (customer). If they are, redirect them to
      // the legacy shipper route.
      redirect = {
        name: 'legacy',
        query: { tenancy: (await Session.instance).tenancy.id, token: await Session.token },
        replace: true
      }
    }

    next(redirect)
  }
}
</script>

<style lang="scss" scoped>
  @import "variables";

  section {
    width: 100vw;
    height: 100vh;
    overflow: hidden;

    > main {
      display: block;
      height: calc(100vh - (#{$topbar-height} + #{$header-height}));
      overflow-x: hidden;
      overflow-y: scroll;
      overscroll-behavior-y: contain;
      box-sizing: border-box;
    }

    // When the sidebar is in floating mode, ensure that
    // the main content is offset by the collapsed width
    // of the sidebar.
    > aside.is-floating + main {
      margin-left: $sidebar-width;
    }
  }
</style>
